import { useEffect, useMemo, useState } from "react";
import { api } from "../api/apiClient";
import type { CatalogItem, Location } from "../api/types";
import { money } from "../i18n/intl";
import {
  newDeliveryLineDraft,
  typedLinesTotal,
  type DeliveryLineDraft,
} from "../utils/deliveryLineDrafts";
import { ivaBasisOptions } from "../utils/purchaseMoneyText";
import { basisAfterEdit, basisForPrefill } from "../utils/typedMoneyBasis";
import { ItemPicker } from "./ItemPicker";
import { SegmentedControl } from "./ui/SegmentedControl";

interface DeliveryLinesEditorProps {
  locationId: string;
  lines: DeliveryLineDraft[];
  onChange: (next: DeliveryLineDraft[]) => void;
}


export function DeliveryLinesEditor({
  locationId,
  lines,
  onChange,
}: DeliveryLinesEditorProps) {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [location, setLocation] = useState<Location | null>(null);

  useEffect(() => {
    void api
      .listItems()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (!locationId) {
      setLocation(null);
      return;
    }
    void api
      .listLocations()
      .then((rows) => {
        setLocation(rows.find((l) => l.id === locationId) ?? null);
      })
      .catch(() => setLocation(null));
  }, [locationId]);

  const itemMap = useMemo(() => {
    const m = new Map<string, CatalogItem>();
    for (const i of items) m.set(i.id, i);
    return m;
  }, [items]);

  const usedItemIds = useMemo(
    () => new Set(lines.map((l) => l.itemId).filter(Boolean)),
    [lines],
  );

  const updateLine = (uid: string, patch: Partial<DeliveryLineDraft>) => {
    onChange(lines.map((l) => (l.uid === uid ? { ...l, ...patch } : l)));
  };

  const removeLine = (uid: string) => {
    onChange(lines.filter((l) => l.uid !== uid));
  };

  const addLine = () => {
    onChange([...lines, newDeliveryLineDraft()]);
  };

  /** G-121 S3: null once a priced line includes IVA — the server works it out. */
  const totalBeforeIva = typedLinesTotal(lines);

  const handleItemPick = (uid: string, itemId: string) => {
    if (!itemId || !location) {
      updateLine(uid, { itemId, zoneId: "" });
      return;
    }
    const item = itemMap.get(itemId);
    if (!item) {
      updateLine(uid, { itemId, zoneId: "" });
      return;
    }
    const preferred = item.isPerishable
      ? location.zones.find((z) => z.zoneType === "FRIDGE")
      : location.zones.find((z) => z.zoneType === "DRY_STORAGE");
    const cost = item.defaultPurchaseUnitCostEur ?? "";
    updateLine(uid, {
      itemId,
      zoneId: preferred?.id ?? "",
      expectedUnitCostEur: cost,
      // G-121 S3: a stored cost is before IVA; no cost, no side yet.
      moneyBasis: basisForPrefill(cost),
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ margin: 0 }}>Lines</h3>
        <button
          type="button"
          className="button tinted"
          onClick={addLine}
          disabled={!locationId}
        >
          + Add line
        </button>
      </div>

      {lines.length === 0 ? (
        <div className="empty-state" style={{ padding: "var(--space-4)" }}>
          {locationId
            ? "Add at least one item line to schedule a delivery."
            : "Pick a shop first."}
        </div>
      ) : (
        lines.map((line, idx) => {
          const item = itemMap.get(line.itemId);
          const filterFn = (i: CatalogItem) =>
            !usedItemIds.has(i.id) || i.id === line.itemId;
          return (
            // D-74: the line's layout lives in `.delivery-line` (base.css), which
            // stacks it at phone and tablet width.
            <div key={line.uid} className="delivery-line">
              <div className="muted delivery-line-number">#{idx + 1}</div>
              <div className="delivery-line-fields">
                <ItemPicker
                  value={line.itemId}
                  onChange={(id) => handleItemPick(line.uid, id)}
                  required
                  filter={filterFn}
                />
                <div className="field">
                  <label>Zone *</label>
                  <select
                    value={line.zoneId}
                    onChange={(e) =>
                      updateLine(line.uid, { zoneId: e.target.value })
                    }
                    disabled={!location}
                  >
                    <option value="">Select a zone</option>
                    {location?.zones.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.name} ({z.zoneType})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>
                    Expected qty
                    {item ? ` (${item.defaultUom})` : ""}
                  </label>
                  <input
                    type="number"
                    min={0.001}
                    step="0.001"
                    value={line.expectedQty}
                    onChange={(e) =>
                      updateLine(line.uid, { expectedQty: e.target.value })
                    }
                  />
                </div>
                <div className="field">
                  <label>Unit cost (€)</label>
                  <input
                    type="number"
                    min={0}
                    step="0.0001"
                    value={line.expectedUnitCostEur}
                    onChange={(e) =>
                      updateLine(line.uid, {
                        expectedUnitCostEur: e.target.value,
                        moneyBasis: basisAfterEdit(line.moneyBasis, e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              <button
                type="button"
                className="button plain delivery-line-remove"
                onClick={() => removeLine(line.uid)}
                aria-label="Remove line"
              >
                Remove
              </button>
              {/* G-121 S3: once there is an item and a cost to qualify — a row of its
                own under the line (`grid-column: 1 / -1`), so it never squeezes the
                inputs or the Remove button at phone width. */}
              {item && line.expectedUnitCostEur.trim() !== "" ? (
                <div className="delivery-line-iva">
                  <span className="muted">Unit cost is</span>
                  <SegmentedControl
                    options={ivaBasisOptions(item.category.vatRatePercent)}
                    value={line.moneyBasis}
                    onChange={(next) => updateLine(line.uid, { moneyBasis: next })}
                    ariaLabel={`Line ${idx + 1}: is the unit cost before IVA or does it include IVA?`}
                  />
                </div>
              ) : null}
            </div>
          );
        })
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--space-2)",
          padding: "var(--space-2) var(--space-3)",
        }}
      >
        <span className="muted" style={{ fontSize: 13 }}>
          {totalBeforeIva === null
            ? "A price includes IVA or has no side yet: the total is worked out when you save."
            : "Live total: qty × unit cost."}
        </span>
        <strong className="delivery-lines-total" style={{ fontSize: 18 }}>
          Total before IVA: {money(totalBeforeIva)}
        </strong>
      </div>
    </div>
  );
}
